import { useState, useEffect, useRef, useCallback } from 'react';
import {
  AdyenCheckout,
  Card,
  ApplePay,
  GooglePay,
  Dropin,
} from '@adyen/adyen-web';
import '@adyen/adyen-web/styles/adyen.css';
import '../../adyen-styles.css';
import getAdyenEnvironment from 'helpers/getAdyenEnvironment';

const useAdyenPayment = (config) => {
  const [checkout, setCheckout] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const initializedKeyRef = useRef(false);
  const runIdRef = useRef(0);

  const initializeAdyen = async () => {
    const runId = ++runIdRef.current;
    const isProd = process.env.REACT_APP_ENV === 'production';
    const environment = getAdyenEnvironment(isProd, config.countryCode);
    setIsLoading(true);

    try {
      console.log(`Initializing Adyen with ${config.toPay}...`);

      const filteredPaymentMethods = config.isSaveCard
        ? {
            ...config.paymentMethods,
            paymentMethods: (
              config.paymentMethods?.paymentMethods || []
            ).filter((pm) => !['applepay', 'googlepay'].includes(pm.type)),
          }
        : config.paymentMethods;

      const checkoutInstance = await AdyenCheckout({
        environment,
        clientKey: config.clientKey,
        countryCode: config.countryCode,
        amount: {
          value: config.toPay,
          currency: config.currency,
        },
        locale: 'en-US',
        paymentMethodsResponse: filteredPaymentMethods,
        onPaymentCompleted: (result) => {
          console.info('Payment completed', result);
        },
        translations: config.translations,
      });

      if (runId !== runIdRef.current) {
        console.log(
          `Discarding stale Adyen checkout init with ${config.toPay}`
        );
        return;
      }
      console.log(`Adyen Initialized with ${config.toPay}...`);
      setCheckout(checkoutInstance);
    } catch (error) {
      console.error('Failed to initialize Adyen:', error);
    } finally {
      if (runId === runIdRef.current) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    const shouldInitialize =
      config.paymentMethods && (config.toPay || config.isSaveCard);

    if (!shouldInitialize) return;

    const key = JSON.stringify({
      paymentMethods: config.paymentMethods,
      toPay: config.toPay,
      currency: config.currency,
      isSaveCard: config.isSaveCard,
    });

    if (initializedKeyRef.current === key) return;

    initializedKeyRef.current = key;
    initializeAdyen();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.toPay, config.isSaveCard, JSON.stringify(config.paymentMethods)]);

  const mountComponent = useCallback(
    ({ paymentMethod, containerId, toPay, ...restProps }) => {
      if (!checkout || !paymentMethod || !containerId) {
        console.warn('Missing required parameters for mountComponent');
        return;
      }

      const container = document.getElementById(containerId);
      if (!container) {
        console.warn(`Container with id ${containerId} not found`);
        return;
      }

      const cardStyle = {
        base: {
          color: '#222',
          '::placeholder': {
            color: '#aaa',
          },
          backgroundColor: '#ffffff',
          '::selection': {
            backgroundColor: '#000000',
            color: '#ffffff',
          },
        },
        error: {
          color: '#e53935',
        },
        validated: {
          color: '#43a047',
        },
        focus: {
          color: '#000000',
        },
        placeholder: {
          color: '#aaa',
        },
      };

      const applePayConfig = {
        buttonType: 'plain',
      };

      const googlePayConfig = {
        buttonType: 'pay',
        buttonSizeMode: 'fill',
      };

      const amountConfig = {
        value: toPay ?? config.toPay,
        currency: config.currency,
      };

      const dropinConfig = {
        showStoredPaymentMethods: true,
        paymentMethodsConfiguration: {
          applepay: {
            amount: amountConfig,
          },
          googlepay: {
            amount: amountConfig,
            configuration: {
              merchantName: config.storeName,
              gatewayMerchantId: config.adyenMerchantAccount,
              merchantId: config.gpayMerchantId,
              authJwt: config.gpayJWTToken,
              merchantOrigin:
                typeof window !== 'undefined' ? window.location.hostname : '',
            },
          },
          card: {
            amount: amountConfig,
            enableStoreDetails: config.isSaveCard,
          },
        },
        paymentMethodComponents: [Card, ApplePay, GooglePay],
      };

      try {
        let component;
        switch (paymentMethod) {
          case 'dropin':
            component = new Dropin(checkout, {
              ...dropinConfig,
              ...restProps,
            });
            break;
          case 'card':
            component = new Card(checkout, {
              styles: cardStyle,
              showStoredPaymentMethods: true,
              ...restProps,
            });
            break;
          case 'applepay':
            component = new ApplePay(checkout, {
              ...applePayConfig,
              ...restProps,
            });
            break;
          case 'googlepay':
            component = new GooglePay(checkout, {
              ...googlePayConfig,
              ...restProps,
            });
            break;

          default:
            console.warn(`Unsupported payment method: ${paymentMethod}`);
            return;
        }

        component.mount(`#${containerId}`);

        return component;
      } catch (err) {
        console.error(`Failed to mount ${paymentMethod}:`, err);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [checkout]
  );

  return { checkout, isLoading, mountComponent };
};

export default useAdyenPayment;
